import { ChevronDownIcon } from "@storytell/bsky-icons";
import { Features } from "@storytell/cp-client";
import { StPopover, StPopoverTrigger } from "@storytell/ui/popover";
import { type Component, For, Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import { getModelDisplayName } from "~/utils/modelMapping";
import { MODEL_VENDORS, getModelVendor } from "~/utils/modelVendors";
import { usePromptBarContext } from "../PromptBarContext";
import { PromptBarButton } from "./PromptBarButton";

interface ModelOption {
  value: Features.Feature;
  label: string;
  description?: string;
}

interface ModelGroup {
  vendor: string;
  models: ModelOption[];
}

const MODEL_GROUPS: ModelGroup[] = [
  {
    vendor: "storytell",
    models: [
      {
        value: Features.Feature.MODEL_STORYTELL_AUTO_SELECT,
        label: "Auto Select",
        description: "Let Storytell choose the best model",
      },
    ],
  },
  {
    vendor: "anthropic",
    models: [
      {
        value: Features.Feature.MODEL_ANTHROPIC_CLAUDE_4_SONNET_THINKING,
        label: "Claude 4 Sonnet",
        description: "Advanced reasoning capabilities",
      },
    ],
  },
  {
    vendor: "openai",
    models: [
      {
        value: Features.Feature.MODEL_OPENAI_GPT_5,
        label: "GPT-5",
        description: "Next-generation model",
      },
      {
        value: Features.Feature.MODEL_OPENAI_GPT_5_MINI,
        label: "GPT-5 Mini",
        description: "Balanced performance",
      },
      {
        value: Features.Feature.MODEL_OPENAI_GPT_5_NANO,
        label: "GPT-5 Nano",
        description: "Lightweight, fast",
      },
      {
        value: Features.Feature.MODEL_OPENAI_03,
        label: "o3",
        description: "Specialized reasoning model",
      },
    ],
  },
  {
    vendor: "google",
    models: [
      {
        value: Features.Feature.MODEL_GOOGLE_GEMINI_20_FLASH_LITE,
        label: "Gemini 2.0 Flash Lite",
        description: "Lightweight, fast",
      },
      {
        value: Features.Feature.MODEL_GOOGLE_GEMINI_25_FLASH,
        label: "Gemini 2.5 Flash",
        description: "Fast multimodal model",
      },
      {
        value: Features.Feature.MODEL_GOOGLE_GEMINI_25_PRO,
        label: "Gemini 2.5 Pro",
        description: "Advanced capabilities",
      },
    ],
  },
];

/**
 * Model selector component for choosing AI models
 */
export const ModelSelector: Component = () => {
  const { knowledgeSources } = usePromptBarContext();

  const currentModel = () => knowledgeSources.selectedModel() ?? Features.Feature.MODEL_STORYTELL_AUTO_SELECT;
  const currentVendor = () => getModelVendor(currentModel());
  const currentModelName = () => {
    // Try to get display name from mapping first
    const displayName = getModelDisplayName(currentModel());
    if (displayName) return displayName;

    // Fallback to finding in our groups
    for (const group of MODEL_GROUPS) {
      const model = group.models.find((m) => m.value === currentModel());
      if (model) return model.label;
    }

    return "Auto Select";
  };

  return (
    <StPopover
      gutter={10}
      placement="bottom-end"
      noArrow
      content={
        <div class="flex flex-col gap-1 w-64 max-h-96 overflow-y-auto px-1 py-2">
          <For each={MODEL_GROUPS}>
            {(group, groupIndex) => {
              const vendor = MODEL_VENDORS[group.vendor];
              const GroupIcon = vendor.icon;

              return (
                <>
                  <Show when={groupIndex() > 0}>
                    <div class="h-px bg-primary-border my-1" />
                  </Show>
                  <div class="px-2 py-1">
                    <div class="flex items-center gap-2">
                      <GroupIcon size={14} class={vendor.color} />
                      <span class="text-xs font-medium text-secondary-text">{vendor.name}</span>
                    </div>
                  </div>
                  <For each={group.models}>
                    {(model) => {
                      const modelValue = model.value; // Capture value to avoid closure issues
                      return (
                        <button
                          {...(currentModel() === modelValue ? { "data-active": true } : undefined)}
                          class="group/model rounded cursor-pointer flex gap-2 px-2 py-1 data-active:bg-primary dark:data-active:bg-dark-primary data-active:text-on-primary data-active:dark:text-dark-on-primary hover:bg-background-hover hover:dark:bg-dark-background-hover"
                          type="button"
                          onClick={() => {
                            knowledgeSources.setSelectedModel(modelValue);
                          }}
                        >
                          <GroupIcon
                            class={`${vendor.color} group-data-active/model:text-on-primary group-data-active/model:dark:text-dark-on-primary flex-shrink-0 mt-1`}
                            size={16}
                          />
                          <div class="flex flex-col gap-1 items-start text-left">
                            <p class="font-medium text-sm">{model.label}</p>
                            <Show when={model.description}>
                              <p class="text-xs opacity-80">{model.description}</p>
                            </Show>
                          </div>
                        </button>
                      );
                    }}
                  </For>
                </>
              );
            }}
          </For>
        </div>
      }
    >
      <StPopoverTrigger as={PromptBarButton} class={`min-w-0 px-2 text-[0.8125rem] ${currentVendor().color}`}>
        <Dynamic component={currentVendor().icon} size={16} class="flex-shrink-0" />
        <span class="text-on-surface dark:text-dark-on-surface truncate max-w-[120px] sm:max-w-[150px]">
          {currentModelName()}
        </span>
        <ChevronDownIcon size={12} class="ml-1 opacity-60 flex-shrink-0" />
      </StPopoverTrigger>
    </StPopover>
  );
};
